const cadWhole = new Intl.NumberFormat("en-CA", {
  style: "currency",
  currency: "CAD",
  maximumFractionDigits: 0,
});

/**
 * CELL-CURRENCY-LOCALE-1 (s208, D-206.18) — the ONE formatter for a planner CURRENCY CELL.
 *
 * 🔴 MOVED to `shared/currency.ts` at s209 and re-exported here, so this module stays the import
 * every client surface already reaches for. It moved because `shared/cell-text.ts` — the shared
 * "what text does a person read for this cell?" answer, reachable from the SERVER document builders
 * — needs it too, and a currency cell that formats on screen while a PDF prints `8000` is one defect
 * with two halves. The doc comment (locale history, the 2-vs-0 decimals ruling, the stated limits)
 * travelled with the function; it is not restated here.
 *
 * The estate-surface helpers below (`formatCADMagnitude`, `formatApproxCAD`, …) stay client-side:
 * nothing on the server prints an estate figure through them.
 */
export { formatPlannerCurrency } from "@shared/currency";

/** Whole-dollar CAD magnitude (no sign) — matches the estate cards' `fmt`. */
export function formatCADMagnitude(n: number): string {
  return cadWhole.format(Math.abs(n));
}

/**
 * Net estate value for display. A negative (insolvent) net renders in
 * parenthesized accounting style, e.g. "($123,000)"; non-negative renders
 * plainly. Never shows a bare abs() of a signed value.
 */
export function formatNetEstateDisplay(n: number): string {
  return n < 0 ? `(${formatCADMagnitude(n)})` : formatCADMagnitude(n);
}

/**
 * APPROX-ESTATE-DISPLAY-1 (s206, Reade's ruling). A figure DERIVED from an approximate
 * input is not printed to the dollar: "About $81,000", never "$81,015". Even under the
 * "Estimate only" label the estate summary already carries, a figure ending in 015 reads
 * as calculated, and the planner's inputs never supported that precision.
 *
 * 🔴 ROUND IT ALWAYS — no override flag and no per-row check. The estate number is
 * ALWAYS an estimate: `estate-summary.ts` labels it "Estimate only", the snapshot card
 * says "Estimated estate value", the executor dashboard says "probate tax estimate", and
 * the executor replaces the figure outright at probate via `estate_value_overrides`.
 * A per-row "was this overridden" test was considered and rejected as false precision.
 *
 * The exact formatters above are RETAINED and still exported — rounding is a decision
 * about the estate surfaces, not about currency rendering everywhere.
 */

/** Round to 3 significant figures, floor of $100. 81015 -> 81000; 5442252 -> 5440000. */
export function roundForApproxDisplay(n: number): number {
  const abs = Math.abs(n);
  if (abs === 0) return 0;
  const step = Math.max(100, Math.pow(10, Math.floor(Math.log10(abs)) - 2));
  return Math.sign(n) * Math.round(abs / step) * step;
}

/** Approximate CAD magnitude (no sign), prefixed "About". Exact zero stays "$0". */
export function formatApproxCAD(n: number): string {
  const rounded = roundForApproxDisplay(n);
  if (rounded === 0) return formatCADMagnitude(0);
  return `About ${formatCADMagnitude(rounded)}`;
}

/** Approximate net estate: negative (insolvent) keeps the parenthesized accounting style. */
export function formatApproxNetEstateDisplay(n: number): string {
  const rounded = roundForApproxDisplay(n);
  if (rounded === 0) return formatCADMagnitude(0);
  const magnitude = `About ${formatCADMagnitude(rounded)}`;
  return rounded < 0 ? `(${magnitude})` : magnitude;
}
